import { useState, useEffect, useRef, useCallback } from 'react';
import type { Swiper as SwiperInstance } from 'swiper';

interface UseGalleryPaginationProps {
  swiperRef: React.RefObject<SwiperInstance | null>;
  enableAutoSwipe: boolean;
  paginationType?: string;
  autoSwipeDelay: number;
  loop: boolean;
  onAutoplayProgressUpdate?: (progress: number) => void;
}

export const useGalleryPagination = ({
                                       swiperRef,
                                       enableAutoSwipe,
                                       paginationType,
                                       autoSwipeDelay,
                                       loop,
                                       onAutoplayProgressUpdate,
                                     }: UseGalleryPaginationProps) => {
  const [activeSlideAutoplayProgress, setActiveSlideAutoplayProgress] = useState(0);
  const [isAutoplayPausedVisual, setIsAutoplayPausedVisual] = useState(true);
  const autoplayProgressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const updateProgress = useCallback(() => {
    if (swiperRef.current?.autoplay?.running) {
      const timeLeft = swiperRef.current.autoplay.timeLeft ?? autoSwipeDelay;
      const progress = Math.max(0, Math.min(1, 1 - timeLeft / autoSwipeDelay));
      setActiveSlideAutoplayProgress(progress);
      if (onAutoplayProgressUpdate) onAutoplayProgressUpdate(progress);
    }
  }, [swiperRef, autoSwipeDelay, onAutoplayProgressUpdate]);

  // --- Interval Management ---
  const stopProgressInterval = useCallback(() => {
    if (autoplayProgressIntervalRef.current) {
      clearInterval(autoplayProgressIntervalRef.current);
      autoplayProgressIntervalRef.current = null;
    }
  }, []);

  const startProgressInterval = useCallback(() => {
    stopProgressInterval(); // Ensure no multiple intervals are running
    autoplayProgressIntervalRef.current = setInterval(updateProgress, 50);
  }, [stopProgressInterval, updateProgress]);

  useEffect(() => {
    if (!enableAutoSwipe || paginationType !== 'customWithProgress') {
      setIsAutoplayPausedVisual(true);
      setActiveSlideAutoplayProgress(0);
      stopProgressInterval();
    }
    return () => {
      stopProgressInterval();
    };
  }, [enableAutoSwipe, paginationType, stopProgressInterval, setActiveSlideAutoplayProgress]);


  // --- Event Handlers (These are now the single source of truth) ---
  const resetAutoplayProgressForNewSlide = useCallback(() => {
    if (paginationType === 'customWithProgress') {
      stopProgressInterval();
      setActiveSlideAutoplayProgress(0);
      if (onAutoplayProgressUpdate) onAutoplayProgressUpdate(0);

      // After a slide changes, re-start the interval only if autoplay is still running.
      if (swiperRef.current?.autoplay?.running) {
        startProgressInterval();
      } else {
        setIsAutoplayPausedVisual(true);
      }
    }
  }, [paginationType, swiperRef, onAutoplayProgressUpdate, startProgressInterval, stopProgressInterval]);

  const handleSwiperAutoplayStart = useCallback(() => {
    if (paginationType === 'customWithProgress') {
      setActiveSlideAutoplayProgress(0);
      if (onAutoplayProgressUpdate) onAutoplayProgressUpdate(0);
      setIsAutoplayPausedVisual(false); // Autoplay has started, update the UI
      startProgressInterval(); // Start the progress animation
    }
  }, [paginationType, onAutoplayProgressUpdate, startProgressInterval]);

  const handleSwiperAutoplayStop = useCallback(() => {
    if (paginationType === 'customWithProgress') {
      setActiveSlideAutoplayProgress(0);
      if (onAutoplayProgressUpdate) onAutoplayProgressUpdate(0);
      setIsAutoplayPausedVisual(true);
      stopProgressInterval();
    }
  }, [paginationType, onAutoplayProgressUpdate, stopProgressInterval]);

  const handleSwiperAutoplayPause = useCallback(() => {
    if (paginationType === 'customWithProgress') {
      setIsAutoplayPausedVisual(true);
      stopProgressInterval();
    }
  }, [paginationType, stopProgressInterval]);

  const handleSwiperAutoplayResume = useCallback(() => {
    if (paginationType === 'customWithProgress') {
      setIsAutoplayPausedVisual(false);
      startProgressInterval();
    }
  }, [paginationType, startProgressInterval]);


  return {
    activeSlideAutoplayProgress,
    isAutoplayPausedVisual,
    resetAutoplayProgressForNewSlide,
    handleSwiperAutoplayStart,
    handleSwiperAutoplayStop,
    handleSwiperAutoplayPause,
    handleSwiperAutoplayResume,
    setActiveSlideAutoplayProgress,
    setIsAutoplayPausedVisual,
  };
};
